import { cycleDayToday } from '../context';
import { addDays, fm } from '../dates';
import { deltaText, mean, num, r1 } from '../format';
import { ORNG, PHASES } from '../palette';
import { NA_CELL, bars, cycleColumn, cycleLabel, cycleLegend, detailSection, heatCell } from './shared';

const WEEKS = 8;

/** Weekly averages over the last 8 calendar weeks, from the raw logs (README gap G12). */
function weeklyEnergy(ctx) {
  const out = [];
  for (let w = WEEKS - 1; w >= 0; w--) {
    const end = addDays(ctx.today, -w * 7);
    const start = addDays(end, -6);
    const values = [];
    for (let d = start; d <= end; d = addDays(d, 1)) {
      const v = ctx.logs.energy[d];
      if (v != null) values.push(v);
    }
    const a = mean(values);
    out.push({ label: fm(start), sub: `${values.length}/7 days`, v: a, txt: a == null ? '' : r1(a), ongoing: false, empty: 'No data' });
  }
  return out;
}

export function energyScreen(ctx, x) {
  const cur = ctx.cur;
  const cyc = cur ? x.comp.concat([cur]) : x.comp;
  const E = ctx.analytics.energy;
  const e = cur ? E[cur.start] : null;
  const cd = e?.cycle_day ?? cycleDayToday(ctx);
  const avgOf = (f) => mean(x.comp.map(f));
  const phaseAvg = (c, p) => E[c.start]?.by_phase?.[p.toLowerCase()]?.average ?? null;
  const phases = PHASES.map((p) => ({ p, v: avgOf((c) => phaseAvg(c, p)) })).filter((p) => p.v != null);
  const low = phases.slice().sort((a, b) => a.v - b.v)[0];
  const prevN = cur && cur.n > 1 ? cur.n - 1 : null;

  const kpis = [
    { label: 'Average energy', value: r1(e?.average), unit: e?.average != null ? '/5' : '', scope: cur ? `Cycle ${cur.n} · cycle to date (days 1–${cd})` : 'No cycle yet', note: `${deltaText(e?.average ?? null, e?.comparison?.previous_average ?? null)} vs ${prevN ? `cycle ${prevN}` : "previous cycle"}, days 1–${cd}` },
    e?.lowest_day
      ? { label: 'Lowest-energy day', value: num(e.lowest_day.value), unit: '/5', scope: `${fm(e.lowest_day.date)} · cycle day ${ctx.info(e.lowest_day.date)?.cd ?? '—'}`, note: 'Most recent day if tied' }
      : { label: 'Lowest-energy day', value: '—', unit: '', scope: 'No entries this cycle', note: '' },
    low
      ? { label: 'Lowest-energy phase', value: low.p, unit: '', scope: `${r1(low.v)}/5 avg · ${x.rl}`, note: 'Phase dates from Period card' }
      : { label: 'Lowest-energy phase', value: '—', unit: '', scope: x.rl, note: 'Not enough data' },
    { label: 'Days logged', value: `${e?.days_logged ?? 0} of ${cd ?? 0}`, unit: '', scope: cur ? `Cycle ${cur.n} so far` : '', note: `${(cd ?? 0) - (e?.days_logged ?? 0)} days with no entry` },
  ];

  const avg = avgOf((c) => E[c.start]?.average ?? null);
  const trend = {
    ...bars(
      cyc.map((c) => {
        const v = E[c.start]?.average ?? null;
        return { ...cycleLabel(c), v, txt: r1(v), ongoing: !c.completed, empty: 'No data' };
      }),
      0, 5, '#F26A1F', avg, `Avg ${r1(avg)}`, [0, 1, 2, 3, 4, 5]
    ),
    title: 'Average energy by cycle',
    scope: `Average of logged days · 0–5 · ${x.rl}`,
    legend: cycleLegend('#F26A1F', x.rl),
    hasNote: Boolean(cur),
    note: cur ? `Cycle ${cur.n} is cycle to date (${cd} days) and is not directly comparable to full cycles.` : '',
  };

  const details = [
    detailSection('weekly', 'Additional Energy Trends', 'Weekly average energy · last 8 calendar weeks (not cycle-aligned)', {
      isBlock: false,
      isTrend: true,
      trend: {
        ...bars(weeklyEnergy(ctx), 0, 5, '#F6A774', null, '', [0, 1, 2, 3, 4, 5]),
        title: 'Weekly average energy',
        scope: 'Week starting · days logged shown under each week',
        legend: [],
        hasNote: false,
        note: '',
      },
    }, true),
    detailSection('phase', 'Energy by phase, per cycle', 'Phase averages for every cycle in range', {
      isBlock: true,
      isTrend: false,
      block: {
        type: 'heat',
        corner: 'PHASE',
        cols: cyc.map(cycleColumn),
        rows: PHASES.map((p) => ({
          name: p,
          weight: 500,
          cells: cyc.map((c) => {
            const v = phaseAvg(c, p);
            return v == null && !c.completed && p === 'Luteal' ? NA_CELL : heatCell(v, 5, ORNG, r1);
          }),
        })),
        foot: '“n/a” = phase unavailable until the next period is recorded.',
      },
    }, false),
  ];
  return { kpis, lists: [], trend, details };
}
